import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { Plus, Trash2, MessageSquare, Pencil, Landmark, RotateCcw, Lightbulb } from 'lucide-react';
import { Dialog } from '../../components/ui';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  PageHead, FigureStrip, Figure, Register, DetailDrawer, AcStatus, Field, FormRow, FilterChips, fmtDate,
} from './design';
import { CiActions, ToImprovement, ImprovementRegister } from './Improvement';

const SOURCES = ['Phone', 'Email', 'In person', 'Written / letter', 'Online / portal', 'Advocate', 'Other'];
const RELATIONSHIPS = ['Participant', 'Family / representative', 'Advocate', 'Staff', 'Anonymous', 'Other'];
const CATEGORIES = ['Service quality', 'Communication', 'Staff conduct', 'Billing / fees', 'Care & clinical', 'Access / availability', 'Privacy', 'Other'];
const SEVERITIES = ['low', 'medium', 'high', 'critical'];
const STATUSES = ['open', 'acknowledged', 'in_progress', 'resolved', 'closed'];
// The strengthened Quality Standards by their names in the Aged Care Rules 2025 (Part 6). The list
// first used the draft names ("The Person", "Care and Services"); complaints saved then still read
// as the right standard, because a stored value is matched on its number.
const STANDARDS = ['Standard 1 — The individual', 'Standard 2 — The organisation', 'Standard 3 — The care and services', 'Standard 4 — The environment', 'Standard 5 — Clinical care', 'Standard 6 — Food and nutrition', 'Standard 7 — The residential community'];
const standardName = (v) => { const m = /^Standard ([1-7])/.exec(v || ''); return m ? STANDARDS[Number(m[1]) - 1] : v; };
// Severity is not a status, so it is not in AcStatus; the same badge classes carry it.
const SEVERITY_BADGE = { low: 'badge-gray', medium: 'badge-blue', high: 'badge-yellow', critical: 'badge-red' };
const cap = (s) => String(s || '').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
const isoToday = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
// Render a synthetic option so an out-of-set stored value (import/API write) stays selected instead of silently snapping to option 1.
const Extra = ({ value, list }) => (value && !list.includes(value) ? <option value={value}>{value}</option> : null);

// Row urgency: rose once it has gone to the Commission, amber while it is open and nobody has
// acknowledged it — the acknowledgement is the first thing an auditor looks for.
const complaintTone = (c) => {
  if (c.escalated) return 'rose';
  if (c.status === 'open' && !c.acknowledged_at) return 'amber';
  return undefined;
};

function Severity({ value }) {
  if (!value) return <span className="text-slate-300">—</span>;
  return <span className={clsx('badge', SEVERITY_BADGE[value] || 'badge-gray')}>{cap(value)}</span>;
}

function TextBlock({ label, text }) {
  return (
    <div>
      <dt className="text-[12px] font-medium text-slate-500">{label}</dt>
      <dd className="text-[13px] text-slate-800 whitespace-pre-wrap mt-1">{text || <span className="text-slate-500">Nothing recorded.</span>}</dd>
    </div>
  );
}

export default function AgedCareComplaints({ clientId: only = '', embedded = false } = {}) {
  const [list, setList] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [modal, setModal] = useState(null);
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  const [viewingId, setViewingId] = useState(null);
  const [escalating, setEscalating] = useState(null); // { complaint, reference } while the escalation dialog is open
  const [escalatingBusy, setEscalatingBusy] = useState(false);
  const confirm = useConfirm();

  useEffect(() => { api.get('/aged-care/clients').then((r) => setClients(r.data || [])).catch(() => {}); }, []);
  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try { const r = await api.get('/aged-care/complaints', { params: filter ? { status: filter } : {} }); setList(r.data || []); }
    catch (e) { setList([]); setLoadError(e.response?.data?.message || 'Could not load the complaints'); } finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);
  const visible = only ? list.filter((c) => String(c.client_id) === String(only)) : list;

  const openNew = () => { setF({ client_id: only || '', source: 'Phone', complainant: '', complainant_relationship: 'Participant', category: CATEGORIES[0], summary: '', severity: 'medium', status: 'open', raised_at: isoToday(), acknowledged_at: '', resolution: '', resolved_at: '', follow_up: '', quality_standard: '' }); setModal({}); };
  const openEdit = (c) => { setF({ ...c, quality_standard: c.quality_standard ? standardName(c.quality_standard) : '', raised_at: c.raised_at ? c.raised_at.slice(0, 10) : '', acknowledged_at: c.acknowledged_at ? c.acknowledged_at.slice(0, 10) : '', resolved_at: c.resolved_at ? c.resolved_at.slice(0, 10) : '' }); setModal({ editing: c }); };
  const submit = async () => {
    if (!f.summary || !f.summary.trim()) return toast.error('Summary is required');
    setSaving(true);
    const body = {};
    const keys = ['client_id', 'source', 'complainant', 'complainant_relationship', 'category', 'summary', 'severity', 'status', 'raised_at', 'acknowledged_at', 'resolution', 'resolved_at', 'follow_up', 'quality_standard'];
    if (modal.editing) keys.push('acqsc_reference'); // let a wrong ACQSC reference be corrected via edit
    keys.forEach((k) => { body[k] = f[k] === '' ? null : f[k]; });
    try {
      if (modal.editing) await api.put(`/aged-care/complaints/${modal.editing.id}`, body);
      else await api.post('/aged-care/complaints', body);
      toast.success('Complaint saved'); setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save the complaint'); } finally { setSaving(false); }
  };
  // Sets the escalated flag and stores a hand-typed reference. It does not lodge anything with
  // the Commission; the complaint is escalated through the ACQSC's own channels first.
  const escalate = (c) => setEscalating({ complaint: c, reference: c.acqsc_reference || '' });
  const closeEscalate = useCallback(() => setEscalating(null), []);
  const saveEscalation = async () => {
    if (!escalating) return;
    const { complaint: c, reference } = escalating;
    setEscalatingBusy(true);
    try {
      await api.patch(`/aged-care/complaints/${c.id}/escalate`, { acqsc_reference: reference.trim() || null });
      toast.success('Recorded as escalated to the ACQSC');
      setEscalating(null);
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not record the escalation. Try again.'); } finally { setEscalatingBusy(false); }
  };
  // Escalation recorded by mistake: the flag comes off. The reference is kept on the record, so
  // escalating it again later does not mean typing it again.
  const unescalate = async (c) => {
    const ok = await confirm('The complaint is no longer flagged as escalated to the ACQSC. The reference you recorded is kept, in case it is escalated again.', { title: 'Take off the escalation?', confirmLabel: 'Take off escalation', danger: true });
    if (!ok) return;
    try {
      await api.patch(`/aged-care/complaints/${c.id}/escalate`, { undo: true });
      toast.success('Escalation taken off');
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not take off the escalation'); }
  };
  const del = async (c) => {
    const ok = await confirm(`"${c.summary || 'This complaint'}" is taken off the register as entered in error or a duplicate. The record itself is kept for the retention period.`, { title: 'Remove this complaint from the register?', confirmLabel: 'Remove', danger: true });
    if (!ok) return;
    try {
      await api.delete(`/aged-care/complaints/${c.id}`, { data: { reason: 'entered in error or a duplicate' } });
      toast.success('Complaint removed from the register. The record is kept.');
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not remove the complaint'); }
  };
  // The improvement register sits at the foot of this screen; "added to the improvement register"
  // messages from incidents and complaints point here, so the page head offers the way down.
  const toRegister = () => {
    const el = document.getElementById('improvement-register');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const open = list.filter((c) => !['resolved', 'closed'].includes(c.status)).length;
  const unack = list.filter((c) => c.status === 'open' && !c.acknowledged_at).length;
  const escalated = list.filter((c) => c.escalated).length;
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const editing = modal && modal.editing ? modal.editing : null;
  const viewing = viewingId ? list.find((c) => c.id === viewingId) : null;
  const record = editing || viewing || null;
  // Stable, or the drawer and dialog re-run their focus effects on every keystroke in the form and
  // pull focus out of the field being typed in.
  const closeDrawer = useCallback(() => { setModal(null); setViewingId(null); }, []);
  const closeModal = useCallback(() => setModal(null), []);

  const columns = [
    { key: 'summary', header: 'Summary', truncate: true, sortable: true, render: (c) => (
      <span className="block min-w-0">
        <span className="block truncate font-medium text-slate-800">{c.summary}</span>
        {c.escalated && <span className="block text-[11px] text-red-700">Escalated to the ACQSC{c.acqsc_reference ? `, reference ${c.acqsc_reference}` : ''}</span>}
        {c.quality_standard && <span className="block text-[11px] text-slate-500 truncate">{standardName(c.quality_standard).replace(' — ', ': ')}</span>}
      </span>
    ) },
    { key: 'client_name', header: 'Participant', truncate: true, sortable: true, render: (c) => c.client_name || <span className="text-slate-300">—</span> },
    { key: 'category', header: 'Category', truncate: true, sortable: true, render: (c) => c.category || <span className="text-slate-300">—</span> },
    { key: 'severity', header: 'Severity', width: 105, sortable: true, sortValue: (c) => SEVERITIES.indexOf(c.severity), render: (c) => <Severity value={c.severity} /> },
    { key: 'status', header: 'Status', width: 125, sortable: true, render: (c) => <AcStatus status={c.status} /> },
    { key: 'raised_at', header: 'Raised', width: 115, sortable: true, defaultDir: 'desc', render: (c) => <span className="tabular-nums text-slate-500 whitespace-nowrap">{fmtDate(c.raised_at)}</span> },
    { key: 'acknowledged_at', header: 'Acknowledged', width: 125, sortable: true, render: (c) => (
      c.acknowledged_at
        ? <span className="tabular-nums text-slate-500 whitespace-nowrap">{fmtDate(c.acknowledged_at)}</span>
        : <span className={clsx('whitespace-nowrap', c.ack_overdue ? 'text-red-800 font-medium' : c.status === 'open' ? 'text-amber-700 font-medium' : 'text-slate-500')}>{c.ack_due ? `By ${fmtDate(c.ack_due)}` : 'Not yet'}</span>
    ) },
    { key: 'resolve_due', header: 'Resolve by', width: 115, sortable: true, render: (c) => (
      ['resolved', 'closed'].includes(c.status) ? <span className="text-slate-400">—</span>
        : <span className={clsx('tabular-nums whitespace-nowrap', c.resolve_overdue ? 'text-red-800 font-medium' : 'text-slate-600')}>{fmtDate(c.resolve_due)}</span>
    ) },
  ];

  const form = (
    <div className="space-y-4">
      <Field label="Summary" required>
        {(id) => <textarea id={id} rows={2} className="input-field" value={f.summary || ''} onChange={set('summary')} placeholder="What the complaint is about" />}
      </Field>
      <FormRow cols={2}>
        <Field label="Participant">
          {(id) => (
            <select id={id} className="input-field" value={f.client_id || ''} onChange={set('client_id')}>
              <option value="">Not linked to a participant</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
        </Field>
        <Field label="Category">
          {(id) => <select id={id} className="input-field" value={f.category || ''} onChange={set('category')}><Extra value={f.category} list={CATEGORIES} />{CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</select>}
        </Field>
        <Field label="Raised by">{(id) => <input id={id} className="input-field" value={f.complainant || ''} onChange={set('complainant')} placeholder="Name" />}</Field>
        <Field label="Relationship">
          {(id) => <select id={id} className="input-field" value={f.complainant_relationship || ''} onChange={set('complainant_relationship')}><Extra value={f.complainant_relationship} list={RELATIONSHIPS} />{RELATIONSHIPS.map((r) => <option key={r} value={r}>{r}</option>)}</select>}
        </Field>
        <Field label="Source">
          {(id) => <select id={id} className="input-field" value={f.source || ''} onChange={set('source')}><Extra value={f.source} list={SOURCES} />{SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}</select>}
        </Field>
        <Field label="Raised on">{(id) => <input id={id} type="date" className="input-field" value={f.raised_at || ''} onChange={set('raised_at')} />}</Field>
        <Field label="Severity">
          {(id) => <select id={id} className="input-field" value={f.severity || 'medium'} onChange={set('severity')}>{SEVERITIES.map((s) => <option key={s} value={s}>{cap(s)}</option>)}</select>}
        </Field>
        <Field label="Status">
          {(id) => <select id={id} className="input-field" value={f.status || 'open'} onChange={set('status')}>{STATUSES.map((s) => <option key={s} value={s}>{cap(s)}</option>)}</select>}
        </Field>
        <Field label="Acknowledged on">{(id) => <input id={id} type="date" className="input-field" value={f.acknowledged_at || ''} onChange={set('acknowledged_at')} />}</Field>
        <Field label="Resolved on">{(id) => <input id={id} type="date" className="input-field" value={f.resolved_at || ''} onChange={set('resolved_at')} />}</Field>
      </FormRow>
      <Field label="Linked Quality Standard" hint="The Strengthened Quality Standard this complaint is evidence against.">
        {(id) => <select id={id} className="input-field" value={f.quality_standard || ''} onChange={set('quality_standard')}><option value="">Not linked</option>{STANDARDS.map((s) => <option key={s} value={s}>{s}</option>)}</select>}
      </Field>
      {editing && f.escalated && (
        <Field label="ACQSC reference" hint="The reference the Commission gave you. Typed by hand; nothing is looked up.">
          {(id) => <input id={id} className="input-field" value={f.acqsc_reference || ''} onChange={set('acqsc_reference')} placeholder="Commission reference number" />}
        </Field>
      )}
      <Field label="Resolution">{(id) => <textarea id={id} rows={2} className="input-field" value={f.resolution || ''} onChange={set('resolution')} placeholder="How it was resolved" />}</Field>
      <Field label="Follow-up">{(id) => <textarea id={id} rows={2} className="input-field" value={f.follow_up || ''} onChange={set('follow_up')} />}</Field>
    </div>
  );

  return (
    <div>
      {!embedded && <>
      <PageHead icon={MessageSquare}
        title="Complaints and feedback"
        purpose="Record every complaint, track acknowledgement and resolution, and link each one to the Quality Standard it touches. Escalating a complaint here flags the record and stores the reference the Commission gave you; it does not lodge anything with the ACQSC."
        actions={(
          <>
            <button type="button" onClick={toRegister} className="btn-secondary"><Lightbulb size={15} /> Go to the improvement register</button>
            <button type="button" onClick={openNew} className="btn-primary"><Plus size={15} /> Log complaint</button>
          </>
        )}
      />

      <FigureStrip>
        <Figure label="Open complaints" value={open} />
        <Figure label="Awaiting acknowledgement" value={unack} tone={unack ? 'amber' : 'muted'} />
        <Figure label="Escalated to the ACQSC" value={escalated} tone={escalated ? 'red' : 'muted'} />
        <Figure label="Resolved or closed" value={list.length - open} />
      </FigureStrip>
      </>}

      <Register
        title={embedded ? 'Complaints and feedback' : 'Complaints register'} count={visible.length} countLabel={visible.length === 1 ? 'complaint' : 'complaints'}
        actions={embedded ? <button type="button" onClick={openNew} className="btn-secondary btn-sm"><Plus size={14} /> Log complaint</button> : undefined}
        chips={(
          <FilterChips
            multi={false} ariaLabel="Filter by status" value={filter || null} onChange={(v) => setFilter(v || '')}
            options={[{ value: '', label: 'All' }, ...STATUSES.map((s) => ({ value: s, label: cap(s) }))]}
          />
        )}
        columns={columns} rows={visible} rowKey="id" loading={loading} error={loadError} onRetry={load}
        onRowClick={(c) => setViewingId(c.id)} activeRowKey={record ? record.id : null} rowTone={complaintTone}
        rowActions={(c) => (
          <>
            {!c.escalated && (
              <button type="button" onClick={(e) => { e.stopPropagation(); escalate(c); }} className="btn-ghost btn-sm">
                <Landmark size={14} /> Record escalation
              </button>
            )}
            <button type="button" onClick={(e) => { e.stopPropagation(); openEdit(c); }} className="btn-ghost btn-sm" aria-label="Edit complaint"><Pencil size={14} /></button>
            <button type="button" onClick={(e) => { e.stopPropagation(); del(c); }} className="btn-ghost btn-sm hover:text-red-700" aria-label="Delete complaint"><Trash2 size={14} /></button>
          </>
        )}
        empty={{
          icon: MessageSquare,
          title: filter ? `No ${cap(filter).toLowerCase()} complaints` : 'No complaints logged',
          hint: filter ? 'Clear the status filter to see the whole register.' : 'Record a complaint the day it is raised, then acknowledge it, so the register shows how quickly you responded.',
          action: filter ? undefined : <button type="button" onClick={openNew} className="btn-primary"><Plus size={15} /> Log complaint</button>,
        }}
      />

      {record && (
        <DetailDrawer
          open onClose={closeDrawer} icon={MessageSquare} width="lg"
          title={editing ? 'Edit complaint' : record.summary}
          subtitle={editing ? record.summary : (record.client_name || 'Not linked to a participant')}
          statusNode={<AcStatus status={record.status} />}
          meta={editing ? undefined : [
            { label: 'Raised', value: fmtDate(record.raised_at) },
            { label: 'Raised by', value: record.complainant || null },
            { label: 'Relationship', value: record.complainant_relationship || null },
            { label: 'Source', value: record.source || null },
            { label: 'Category', value: record.category || null },
            { label: 'Severity', value: <Severity value={record.severity} /> },
            { label: 'Acknowledged', value: record.acknowledged_at ? fmtDate(record.acknowledged_at) : 'Not yet' },
            { label: 'Resolved', value: record.resolved_at ? fmtDate(record.resolved_at) : null },
            { label: 'Quality Standard', value: record.quality_standard ? standardName(record.quality_standard) : null },
            record.escalated ? { label: 'ACQSC reference', value: record.acqsc_reference || 'Not recorded' } : null,
          ]}
          footer={editing ? (
            <>
              <button type="button" onClick={() => setModal(null)} className="btn-secondary">Cancel</button>
              <button type="button" onClick={submit} disabled={saving} className="btn-primary">{saving ? 'Saving…' : 'Save changes'}</button>
            </>
          ) : (
            <>
              <button type="button" onClick={() => del(record)} className="btn-ghost text-red-700 hover:text-red-800 mr-auto"><Trash2 size={14} /> Delete</button>
              {record.escalated
                ? <button type="button" onClick={() => unescalate(record)} className="btn-secondary"><RotateCcw size={14} /> Take off escalation</button>
                : <button type="button" onClick={() => escalate(record)} className="btn-secondary"><Landmark size={14} /> Record escalation to the ACQSC</button>}
              <button type="button" onClick={() => openEdit(record)} className="btn-primary"><Pencil size={14} /> Edit</button>
            </>
          )}
        >
          {editing ? form : (
            <div className="space-y-4">
              {record.escalated ? (
                <p className="text-[12.5px] text-slate-500 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
                  This complaint is flagged as escalated to the Aged Care Quality and Safety Commission. The flag and the reference were entered by hand; nothing was sent from this screen. If it was flagged by mistake, use Take off escalation.
                </p>
              ) : (
                <p className="text-[12.5px] text-slate-500 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
                  Escalating to the Commission happens through the ACQSC's own channels. Recording the escalation here flags the complaint and stores the reference you were given; it does not lodge anything.
                </p>
              )}
              <dl className="space-y-4">
                <TextBlock label="Summary" text={record.summary} />
                <TextBlock label="Resolution" text={record.resolution} />
                <TextBlock label="Follow-up" text={record.follow_up} />
              </dl>
              <CiActions source="complaint" sourceId={record.id} />
              <ToImprovement source="complaint" sourceId={record.id} suggested={`From a complaint: ${String(record.summary || record.category || '').slice(0, 120)}`} />
            </div>
          )}
        </DetailDrawer>
      )}

      <Dialog
        open={!!modal && !editing}
        onClose={closeModal}
        size="lg"
        title="Log a complaint"
        description="Record it the day it is raised. Acknowledgement and resolution dates can be added as they happen."
        footer={(
          <>
            <button type="button" onClick={closeModal} className="btn-secondary">Cancel</button>
            <button type="button" onClick={submit} disabled={saving} className="btn-primary">{saving ? 'Saving…' : 'Log complaint'}</button>
          </>
        )}
      >
        {form}
      </Dialog>

      <Dialog
        open={!!escalating}
        onClose={closeEscalate}
        size="sm"
        title="Record escalation to the ACQSC"
        description="This flags the complaint here as escalated to the Aged Care Quality and Safety Commission. It does not lodge anything with the Commission."
        footer={(
          <>
            <button type="button" onClick={closeEscalate} className="btn-secondary">Cancel</button>
            <button type="button" onClick={saveEscalation} disabled={escalatingBusy} className="btn-primary">
              <Landmark size={15} /> {escalatingBusy ? 'Recording…' : 'Record escalation'}
            </button>
          </>
        )}
      >
        {escalating && (
          <Field label="ACQSC reference" hint="Optional. The reference the Commission gave you, if you have one.">
            {(id) => (
              <input
                id={id}
                className="input-field"
                value={escalating.reference}
                onChange={(e) => { const v = e.target.value; setEscalating((s) => (s ? { ...s, reference: v } : s)); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveEscalation(); } }}
                placeholder="Commission reference number"
              />
            )}
          </Field>
        )}
      </Dialog>

      {!embedded && <ImprovementRegister />}
    </div>
  );
}
